/**
 * Download do PDF original da fatura (cliente dono da conta) —
 * GET /invoices/:id/file devolve o arquivo arquivado no backend.
 *
 * Usa axios direto (não o wrapper de services/api.ts) pelo mesmo motivo do
 * contestation.service: a resposta é binária (responseType 'blob') e, no erro,
 * o backend responde JSON — o interceptor global trataria esse corpo como Blob
 * e perderia a mensagem ("PDF original não disponível" etc.).
 */
import axios from 'axios';

import authToken from './auth_token.service';

const DEFAULT_ERROR = 'Não foi possível baixar a fatura. Tente novamente.';

const parseFilename = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() ?? null;
};

const extractBlobError = async (err: unknown): Promise<string> => {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return 'Falha na conexão com o servidor. Tente novamente mais tarde.';
    }
    const data = err.response.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return parsed?.message || DEFAULT_ERROR;
      } catch {
        return DEFAULT_ERROR;
      }
    }
  }
  return DEFAULT_ERROR;
};

export const downloadInvoiceFileService = async (
  invoiceId: string
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/invoices/${invoiceId}/file`,
      {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${authToken()}` },
      }
    );
    const filename =
      parseFilename(res.headers['content-disposition'] as string | undefined) ??
      `fatura_${invoiceId}.pdf`;
    return { blob: res.data as Blob, filename };
  } catch (err) {
    throw new Error(await extractBlobError(err));
  }
};
